<template>
  <div class="max-w-[968px] mx-auto mt-16 md-1:mt-36 px-16 pb-72 md-1:pb-64">
    <provider-profile-edit-mode-popup
      :modelValue="showEditModePopup"
      :variant="popupVariant"
      :paramData="paramData"
      @close="showEditModePopup = false"
      @toggle-profile-visibility="handleToggleProfileVisibility"
      @unhide-and-preview="handleUnhideAndPreview"
    />
    <poppins-popup v-model="showPhotoPopup" :popupHeader="photoPopupHeader">
      <img
        v-if="profileAccountData?.photoUrl"
        :src="profileAccountData?.photoUrl"
        :alt="photoPopupHeader"
        class="w-[225px] mx-auto"
      />
      <poppins-button v-else variant="primary" class="w-full" value="Ok" @click="showPhotoPopup = false" />
    </poppins-popup>
    <provider-profile-action-bar
      :accountTypeInfo="accountTypeInfo"
      :profileAccountData="profileAccountData"
      :viewerAccountData="viewerAccountData"
      :favoritedSitter="favoritedSitter"
      :isProfileOwner="isProfileOwner"
      :bannerType="bannerType"
      :loading="loading"
      @toggle-profile-visibility="$emit('toggle-profile-visibility')"
      @show-edit-mode-popup="handleShowEditModePopup"
      @show-preview-mode="$emit('show-preview-mode')"
      @remove-preview-mode="$emit('remove-preview-mode')"
      @favoritedSitterChange="favoritedSitterChange"
    />
    <provider-profile-loading-skeleton v-if="loading" />
    <div v-else class="flex flex-col gap-16 mt-16 md-1:flex-row md-1:gap-32">
      <div class="w-full top-128 self-start md-1:max-w-[45%] md-1:sticky">
        <provider-profile-sitter-snapshot
          :profileAccountData="profileAccountData"
          :profileData="profileData"
          :reviewsData="reviewsData"
          :isProfileOwner="isProfileOwner"
          :accountTypeInfo="accountTypeInfo"
          :favoritedSitter="favoritedSitter"
          :paramData="paramData"
          :editModeActive="editModeActive"
          :previewModeActive="previewModeActive"
          @favoritedSitterChange="favoritedSitterChange"
          @togglePopup="togglePopup(true)"
          @show-edit-mode-popup="handleShowEditModePopup"
          @experience-click="handleScroll('experience-section')"
          @pay-rate-click="handleScroll('rates-section')"
          @star-rating-click="handleScroll('reviews-section')"
          @certification-click="handleScroll('certifications-section')"
          @check-click="handleScroll('trust-safety-section')"
          @show-photo-popup="showPhotoPopup = true"
        />
      </div>
      <provider-profile-sections
        ref="profileSections"
        class="w-full md-1:max-w-[55%] mb-[160px] md-1:mb-0"
        :profileAccountData="profileAccountData"
        :viewerAccountData="viewerAccountData"
        :reviewsData="reviewsData"
        :paramData="paramData"
        :accountTypeInfo="accountTypeInfo"
        :isProfileOwner="isProfileOwner"
        :editModeActive="editModeActive"
        :previewModeActive="previewModeActive"
        @view-more-reviews="$emit('view-more-reviews')"
        @show-edit-mode-popup="handleShowEditModePopup"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { FormattedCareType } from '../__types__/CareTypes';

import ProviderProfileActionBar from './ProviderProfileActionBar.vue';
import ProviderProfileLoadingSkeleton from './ProviderProfileLoadingSkeleton.vue';
import ProviderProfileSitterSnapshot from './ProviderProfileSitterSnapshot.vue';
import ProviderProfileSections from './ProviderProfileSections.vue';
import ProviderProfileEditModePopup from './ProviderProfileEditModePopup.vue';
import PoppinsPopup from '@/components/popup/Popup.vue';
import PoppinsButton from '@/components/button/Button.vue';

export default {
  name: 'ProviderProfileContainer',
  components: {
    ProviderProfileActionBar,
    ProviderProfileLoadingSkeleton,
    ProviderProfileSitterSnapshot,
    ProviderProfileSections,
    ProviderProfileEditModePopup,
    PoppinsPopup,
    PoppinsButton,
  },
  props: {
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    viewerAccountData: {
      default: () => ({}),
      type: Object,
    },
    reviewsData: {
      default: () => ({}),
      type: Object,
    },
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    paramData: {
      type: Object,
      default: () => ({}),
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
    previewModeActive: {
      type: Boolean,
      default: false,
    },
    bannerType: {
      type: String,
      default: '',
    },
    isProfileOwner: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      favoritedSitter: this.profileAccountData?.favoritedSitter || false,
      showEditModePopup: false,
      popupVariant: '',
      showPhotoPopup: false,
    };
  },
  computed: {
    profileData(): any {
      const { careType } = this.paramData;
      const formattedCareType = FormattedCareType[careType.toUpperCase()];
      return this.profileAccountData?.profiles[formattedCareType]?.profile;
    },
    photoPopupHeader(): string {
      if (!this.profileAccountData) return '';
      const { displayName, photoUrl } = this.profileAccountData;
      return photoUrl ? `${displayName}'s profile photo` : `${displayName} doesn't have a profile photo`;
    },
  },
  watch: {
    profileAccountData: {
      handler(newVal) {
        this.favoritedSitter = newVal?.favoritedSitter || false;
      },
      deep: true,
    },
  },
  methods: {
    favoritedSitterChange(favorited: boolean): void {
      this.favoritedSitter = favorited;
    },
    togglePopup(val) {
      this.showPopup = val;
    },
    handleScroll(targetId: string): void {
      this.$refs.profileSections.scrollToSection(targetId);
    },
    handleShowEditModePopup(variant: string) {
      this.popupVariant = variant;
      this.showEditModePopup = true;
    },
    handleToggleProfileVisibility() {
      this.showEditModePopup = false;
      this.$emit('toggle-profile-visibility');
    },
    async handleUnhideAndPreview() {
      this.showEditModePopup = false;
      await this.$emit('toggle-profile-visibility', true);
    },
  },
};
</script>
